import React, { useState, useEffect, useRef } from "react";
import { Drawer, Form, Input, Button, Select, Upload, Row, Col, InputNumber, Checkbox, Typography } from "antd";
import { LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import CompanyService from "../../services/CompanyService";
import "./Company.scss";
import { filterOption, openNotification, handlePhonePaste, toTurkishUpper } from "../../utils";
import { RcFile } from "antd/es/upload";
import ImageUpload from "../Uploads/ImageUpload";
import { buildCompanyBillingPayload, findBillingAddress, findCompanyAddress } from "./companyBilling";

const validateOptionalTaxNumber = (_: any, value?: string) => {
  if (!value) {
    return Promise.resolve();
  }
  if (!/^[0-9]*$/.test(value)) {
    return Promise.reject(new Error("Vergi Numarası sadece rakamlardan oluşmalıdır"));
  }
  if (value.length < 10 || value.length > 11) {
    return Promise.reject(new Error("Vergi Numarası 10 veya 11 haneli olmalıdır"));
  }
  return Promise.resolve();
};

interface CompanyEditDrawerProps {
  visible: boolean;
  onClose: () => void;
  companyId: string;
  initialSection?: "billing";
}

const CompanyEditDrawer: React.FC<CompanyEditDrawerProps> = ({ visible, onClose, companyId, initialSection }) => {
  const [form] = Form.useForm();
  const billingSectionRef = useRef<HTMLDivElement | null>(null);
  const [imageId, setImageId] = useState<number | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [cities, setCities] = useState<{ id: number; name: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [billingAddressId, setBillingAddressId] = useState<number | null>(null);
  const [existingData, setExistingData] = useState<any>({});
  const [districts, setDistricts] = useState<{ id: number; name: string }[]>([]);
  const [company, setCompany] = useState<any>([]);
  const [selectedCityId, setSelectedCityId] = useState<number | null>(null);
  const [billingDistricts, setBillingDistricts] = useState<{ id: number; name: string }[]>([]);
  const [selectedBillingCityId, setSelectedBillingCityId] = useState<number | null>(null);
  const hasSupply = Form.useWatch("hasSupply", form);
  const invoiceIssuedToDifferentOrganization = Form.useWatch("invoiceIssuedToDifferentOrganization", form);

  useEffect(() => {
    if (!visible || initialSection !== "billing") {
      return;
    }

    const scrollTimer = window.setTimeout(() => {
      billingSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 250);

    return () => window.clearTimeout(scrollTimer);
  }, [visible, initialSection]);

  useEffect(() => {

    const fetchCompanyDetails = async () => {
      const responseCity = await CompanyService.getStates(225);
      setCities(responseCity.data);

      if (companyId) {
        try {
          const response = await CompanyService.getCompany(companyId);
          const companyData = response.data;
          setExistingData(response.data)
          const companyAddress = findCompanyAddress(companyData.addresses);
          const billingAddress = findBillingAddress(companyData.addresses);
          const resolvedBillingAddress = billingAddress || companyAddress;

          if (companyAddress) {
            form.setFieldsValue({
              address: companyAddress.addressLine1,
              city: companyAddress.city,
              district: companyAddress.state,
            });
          }
          if (billingAddress) {
            setBillingAddressId((billingAddress as any).id);
          }

          // Set other company fields
          form.setFieldsValue({
            name: companyData.name,
            taxOffice: companyData.taxOffice,
            taxNumber: companyData.taxNumber,
            email: companyData.email,
            phoneNumber: companyData.phoneNumber,
            kepAddress: companyData.kepAddress,
            invoiceIssuedToDifferentOrganization: companyData.invoiceIssuedToDifferentOrganization === true,
            billingCompanyName: billingAddress?.companyName || companyData.name,
            billingPhoneNumber: billingAddress?.phone || companyData.phoneNumber,
            billingEmail: companyData.billingEmail || companyData.email,
            billingCity: resolvedBillingAddress?.city || "",
            billingDistrict: resolvedBillingAddress?.state || "",
            billingAddress: resolvedBillingAddress?.addressLine1 || "",
            expenseIncluded: companyData.expenseIncluded ?? null,
            hasSupply: companyData.hasSupply ?? null,
            withholdingRate: companyData.hasSupply === true ? companyData.withholdingRate ?? null : null,
            withholdingCode: companyData.hasSupply === true ? companyData.withholdingCode ?? null : null,
          });

          setImageId(companyData.imageId);
          setCompany(companyData);

          const city = await responseCity.data.find((c: any) => c.name === companyAddress?.city);
          console.log(city);
          setSelectedCityId(city?.id ?? null);
          if (city?.id) {
            getDistricts(city.id);
          }

          const billingCity = await responseCity.data.find((c: any) => c.name === resolvedBillingAddress?.city);
          setSelectedBillingCityId(billingCity?.id ?? null);
          if (billingCity?.id) {
            const billingDistrictResponse = await CompanyService.getDistricts(billingCity.id);
            setBillingDistricts(billingDistrictResponse.data);
          }


          if (companyData.imageId) {
            const imageResponse = await CompanyService.getImage(companyData.imageId);
            console.log(imageResponse.data.preSignUrl);
            setImagePreview(imageResponse.data.preSignUrl);
          }
        } catch (error) {
          console.error("Firma bilgileri yüklenirken hata oluştu:", error);
        }
      }
    };



    fetchCompanyDetails();
  }, [companyId, form]);



  const onFinish = async () => {
    setLoading(true);
    try {
      const values = await form.validateFields();
      const normalizedValues = {
        ...values,
        name: toTurkishUpper(values.name),
        taxOffice: toTurkishUpper(values.taxOffice),
        address: toTurkishUpper(values.address),
        billingCompanyName: toTurkishUpper(values.billingCompanyName),
        billingAddress: toTurkishUpper(values.billingAddress),
      };

      const billingPayload = buildCompanyBillingPayload({
        company: {
          name: normalizedValues.name,
          taxOffice: normalizedValues.taxOffice,
          taxNumber: normalizedValues.taxNumber,
          email: normalizedValues.email,
          phoneNumber: normalizedValues.phoneNumber,
          address: normalizedValues.address,
          city: normalizedValues.city,
          district: normalizedValues.district,
        },
        billing: {
          invoiceIssuedToDifferentOrganization: normalizedValues.invoiceIssuedToDifferentOrganization,
          companyName: normalizedValues.billingCompanyName,
          email: normalizedValues.billingEmail,
          phoneNumber: normalizedValues.billingPhoneNumber,
          address: normalizedValues.billingAddress,
          city: normalizedValues.billingCity,
          district: normalizedValues.billingDistrict,
        },
        existingCompanyAddress: findCompanyAddress(existingData?.addresses),
        existingBillingAddress: findBillingAddress(existingData?.addresses),
      });

      let updatedCompanyData: any = {
        name: normalizedValues.name,
        taxOffice: normalizedValues.taxOffice,
        taxNumber: normalizedValues.taxNumber,
        email: normalizedValues.email?.toLowerCase(),
        phoneNumber: normalizedValues.phoneNumber,
        kepAddress: normalizedValues.kepAddress,
        invoiceIssuedToDifferentOrganization: billingPayload.invoiceIssuedToDifferentOrganization,
        billingEmail: billingPayload.billingEmail,
        expenseIncluded: normalizedValues.expenseIncluded ?? null,
        hasSupply: normalizedValues.hasSupply ?? null,
        withholdingRate: normalizedValues.hasSupply === true ? normalizedValues.withholdingRate ?? null : null,
        withholdingCode: normalizedValues.hasSupply === true ? normalizedValues.withholdingCode ?? null : null,
        companyAddresses: [
          billingPayload.companyAddress,
          billingPayload.billingAddress,
        ],
      };
      updatedCompanyData.imageId = imageId;

      console.log(updatedCompanyData);

      await CompanyService.update(companyId, updatedCompanyData);

      openNotification("success", "Başarılı", "Firma bilgileri güncellendi.");
      onClose();
      setLoading(false);
    } catch (error) {
      openNotification("error", "Hata", "Firma bilgileri güncellenemedi.");
      setLoading(false);
    }
  };


  const getDistricts = async (stateId: number) => {
    try {
      const response = await CompanyService.getDistricts(stateId);
      setDistricts(response.data);
    } catch (error) {
      console.error("İlçeler alınırken hata oluştu:", error);
    }
  };

  const handleCityChange = (value: number) => {
    const city = cities.find((c) => c.id === value);
    setSelectedCityId(value);
    form.setFieldsValue({ city: city?.name, district: undefined });
    setDistricts([]);
    if (value) {
      getDistricts(value);
    }
  };

  const handleDistrictChange = (value: string) => {
    form.setFieldsValue({ district: value });
  };

  const handleBillingCityChange = async (value: number) => {
    const city = cities.find((c) => c.id === value);
    setSelectedBillingCityId(value);
    form.setFieldsValue({ billingCity: city?.name, billingDistrict: undefined });
    setBillingDistricts([]);
    if (!value) {
      return;
    }

    try {
      const response = await CompanyService.getDistricts(value);
      setBillingDistricts(response.data);
    } catch (error) {
      console.error("Fatura ilçeleri alınırken hata oluştu:", error);
    }
  };

  const handleBillingDistrictChange = (value: string) => {
    form.setFieldsValue({ billingDistrict: value });
  };

  return (
    <Drawer
      title="Firma Düzenle"
      width={720}
      onClose={onClose}
      visible={visible}
      bodyStyle={{ paddingBottom: 80 }}
      footer={
        <div className="text-right" >
          <Button type="primary" onClick={onFinish} loading={loading} className="d-button dark-button p-20px width-auto">
            Kaydet ve Tamamla
          </Button>
        </div>
      }
    >
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          {/* <Col span={6}>
            <Form.Item label="Logo" name="logo" valuePropName="file" className="form-item">
            <ImageUpload imageId={company?.imageId} setImageId={setImageId} />
            </Form.Item>
          </Col> */}
          <Col span={24}>
            <Form.Item
              name="name"
              label="Firma Unvanı"
              className="form-item"
              rules={[{ required: true, message: "Firma Unvanı boş bırakılamaz" }]}
            >
              <Input
                placeholder="Firma Unvanı"
                className="d-input d-input-form"
                lang="tr"
                style={{ textTransform: "uppercase" }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="email"
              label="E-posta"
              className="form-item"
              rules={[
                { required: true, message: "E-posta boş bırakılamaz" },
                { type: "email", message: "Geçerli bir e-posta adresi girin" },
              ]}
            >
              <Input
                placeholder="E-posta"
                className="d-input d-input-form "
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="phoneNumber"
              label="Telefon Numarası"
              className="form-item"
              rules={[
                { required: true, message: 'Telefon Numarası boş bırakılamaz' },
                { pattern: /^[1-9]/, message: 'Telefon Numarası 0 ile başlamamalıdır' },
                { pattern: /^\d{10}$/, message: 'Telefon Numarası toplamda 10 haneli olmalıdır' },
              ]}
            >
              <Input
                addonBefore="+90"
                placeholder="Cep Telefonu"
                className="d-input d-input-form"
                maxLength={10}
                onPaste={(e) => handlePhonePaste(e, { form, fieldName: 'phoneNumber' })}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="city"
              label="İl"
              className="form-item"
              rules={[{ required: true, message: "İl seçimi zorunludur" }]}
            >
              <Select
                placeholder="İl seçin"
                className="d-input d-input-form"
                showSearch
                filterOption={filterOption}
                onChange={handleCityChange}

              >
                {cities.map((city) => (
                  <Select.Option key={city.id} value={city.id}>
                    {city.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="district"
              label="İlçe"
              rules={[{ required: true, message: "İlçe seçimi zorunludur" }]}
            >
              <Select
                placeholder="İlçe seçin"
                className="d-input d-input-form"
                showSearch
                filterOption={filterOption}
                onChange={handleDistrictChange}
                disabled={!selectedCityId}
              >
                {districts.map((district) => (
                  <Select.Option key={district.id} value={district.name}>
                    {district.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="address"
              label="Fatura Adresi"
              className="form-item"
            >
              <Input.TextArea
                placeholder="Fatura Adresi"
                className="d-input d-input-form"
                rows={4}
                lang="tr"
                style={{ textTransform: "uppercase" }}
              />
            </Form.Item>
          </Col>
        </Row>
        <div ref={billingSectionRef}>
          <Typography.Title level={5} className="mb-0">Faturalandırma</Typography.Title>
        </div>
        <Form.Item
          name="invoiceIssuedToDifferentOrganization"
          valuePropName="checked"
          className="form-item"
        >
          <Checkbox>Fatura, farklı kişi/kuruluş adına düzenlensin</Checkbox>
        </Form.Item>
        <Typography.Text type="secondary" className="d-block mb-16px">
          İşaretlenmediği takdirde fatura, hizmet sunulacak kuruluş bilgileri kullanılarak düzenlenecektir.
        </Typography.Text>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="taxOffice"
              label="Vergi Dairesi"
              className="form-item"
            >
              <Input
                placeholder="Vergi Dairesi"
                className="d-input d-input-form"
                lang="tr"
                style={{ textTransform: "uppercase" }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="taxNumber"
              label="Vergi Numarası"
              className="form-item"
              rules={[{ validator: validateOptionalTaxNumber }]}
            >
              <Input
                placeholder="Vergi Numarası"
                className="d-input d-input-form"
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="kepAddress"
              label="KEP Adresi"
              className="form-item"
              rules={[
                { type: 'email', message: 'Geçerli bir KEP Adresi girin' }
              ]}
            >
              <Input
                placeholder="KEP Adresi"
                className='d-input d-input-form'
              />
            </Form.Item>
          </Col>
        </Row>
        {invoiceIssuedToDifferentOrganization ? (
          <>
            <Typography.Title level={5}>Fatura Bilgileri</Typography.Title>
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  name="billingCompanyName"
                  label="Firma Unvanı"
                  className="form-item"
                  rules={[{ required: true, message: "Firma Unvanı boş bırakılamaz" }]}
                >
                  <Input
                    placeholder="Firma Unvanı"
                    className="d-input d-input-form"
                    lang="tr"
                    style={{ textTransform: "uppercase" }}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="billingPhoneNumber"
                  label="Telefon Numarası"
                  className="form-item"
                  rules={[
                    { required: true, message: 'Telefon Numarası boş bırakılamaz' },
                    { pattern: /^[1-9]/, message: 'Telefon Numarası 0 ile başlamamalıdır' },
                    { pattern: /^\d{10}$/, message: 'Telefon Numarası toplamda 10 haneli olmalıdır' },
                  ]}
                >
                  <Input
                    addonBefore="+90"
                    placeholder="Telefon Numarası"
                    className="d-input d-input-form"
                    maxLength={10}
                    onPaste={(e) => handlePhonePaste(e, { form, fieldName: 'billingPhoneNumber' })}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="billingEmail"
                  label="E-posta"
                  className="form-item"
                  rules={[
                    { required: true, message: "E-posta boş bırakılamaz" },
                    { type: "email", message: "Geçerli bir e-posta adresi girin" },
                  ]}
                >
                  <Input
                    placeholder="E-posta"
                    className="d-input d-input-form"
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="billingCity"
                  label="İl"
                  className="form-item"
                  rules={[{ required: true, message: "İl seçimi zorunludur" }]}
                >
                  <Select
                    placeholder="İl seçin"
                    className="d-input d-input-form"
                    showSearch
                    filterOption={filterOption}
                    onChange={handleBillingCityChange}
                  >
                    {cities.map((city) => (
                      <Select.Option key={city.id} value={city.id}>
                        {city.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="billingDistrict"
                  label="İlçe"
                  rules={[{ required: true, message: "İlçe seçimi zorunludur" }]}
                >
                  <Select
                    placeholder="İlçe seçin"
                    className="d-input d-input-form"
                    showSearch
                    filterOption={filterOption}
                    onChange={handleBillingDistrictChange}
                    disabled={!selectedBillingCityId}
                  >
                    {billingDistricts.map((district) => (
                      <Select.Option key={district.id} value={district.name}>
                        {district.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  name="billingAddress"
                  label="Fatura Adresi"
                  className="form-item"
                >
                  <Input.TextArea
                    placeholder="Fatura Adresi"
                    className="d-input d-input-form"
                    rows={4}
                    lang="tr"
                    style={{ textTransform: "uppercase" }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </>
        ) : null}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="expenseIncluded"
              label="Masraf Ekleniyor Mu?"
              className="form-item"
            >
              <Select
                allowClear
                placeholder="Seçiniz"
                className="d-input d-input-form"
              >
                <Select.Option value={true}>EVET</Select.Option>
                <Select.Option value={false}>HAYIR</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="hasSupply"
              label="Tevkifat Var Mı?"
              className="form-item"
            >
              <Select
                allowClear
                placeholder="Seçiniz"
                className="d-input d-input-form"
                onChange={(value) => {
                  if (value !== true) {
                    form.setFieldsValue({ withholdingRate: null, withholdingCode: null });
                  }
                }}
              >
                <Select.Option value={true}>EVET</Select.Option>
                <Select.Option value={false}>HAYIR</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="withholdingRate"
              label="Tevkifat Oranı"
              className="form-item"
              rules={[
                { type: "number", min: 0, max: 100, message: "Tevkifat Oranı 0 ile 100 arasında olmalıdır" },
              ]}
            >
              <InputNumber
                min={0}
                max={100}
                precision={2}
                placeholder="Tevkifat Oranı"
                className="d-input d-input-form"
                style={{ width: "100%" }}
                disabled={hasSupply !== true}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="withholdingCode"
              label="Tevkifat Kodu"
              className="form-item"
            >
              <Input.TextArea
                placeholder="Tevkifat Kodu"
                className="d-input d-input-form"
                rows={3}
                disabled={hasSupply !== true}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Drawer>
  );
};

export default CompanyEditDrawer;
